
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from "@/components/layout/Layout";
import { getPosts } from '@/services/postService';
import { Post } from '@/types/board';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Calendar, MessageSquare, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const allPosts = await getPosts();
        
        // Filter posts by search query (case insensitive)
        const filteredPosts = allPosts.filter(post => 
          post.title.toLowerCase().includes(query.toLowerCase()) || 
          post.content.toLowerCase().includes(query.toLowerCase())
        );
        
        setPosts(filteredPosts);
        setError(null);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('게시글을 불러오는데 실패했습니다');
      } finally {
        setLoading(false);
      }
    };
    
    if (query) {
      fetchPosts();
    } else {
      setPosts([]);
      setLoading(false);
    }
  }, [query]);
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR');
  };
  
  return (
    <Layout>
      <div className="container mx-auto py-8">
        <h1 className="text-3xl font-bold mb-6">검색 결과: "{query}"</h1>
        
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card className="mb-6">
            <CardContent className="py-8 text-center text-muted-foreground">
              <p>{error}</p>
            </CardContent>
          </Card>
        ) : posts.length > 0 ? (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card key={post.id} className="hover:bg-muted/50 transition-colors">
                <CardHeader className="py-4">
                  <Link to={`/board/post/${post.id}`}>
                    <CardDescription className="text-primary">{post.category}</CardDescription>
                    <CardTitle className="text-xl mt-1">{post.title}</CardTitle>
                  </Link>
                  <div className="flex items-center text-sm text-muted-foreground mt-2">
                    <span>{post.author_name}</span>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <Calendar className="h-3 w-3 mr-1" />
                    <span>{formatDate(post.created_at)}</span>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <Eye className="h-3 w-3 mr-1" />
                    <span>{post.view_count}</span>
                    <Separator orientation="vertical" className="mx-2 h-4" />
                    <MessageSquare className="h-3 w-3 mr-1" />
                    <span>{post.comment_count}</span>
                  </div>
                </CardHeader>
                <CardContent className="py-2">
                  <p className="text-muted-foreground line-clamp-2">
                    {post.content}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="mb-6">
            <CardContent className="py-8 text-center text-muted-foreground">
              <p>"{query}"에 대한 검색 결과가 없습니다.</p>
            </CardContent>
          </Card>
        )}
        
        <div className="mt-8">
          <Button variant="outline" asChild>
            <Link to="/board">게시판으로 이동</Link>
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default SearchResults;
